import { service } from '@ember/service';
import { t } from 'ember-intl';
import Card from 'tio-ui/components/card';
import Chip from 'tio-ui/components/chip';
import type { ChipIntent } from 'tio-ui/components/chip';
import Component from '@glimmer/component';
import Text from 'tio-ui/components/text';
import { PhCheckCircle, PhWarning } from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import { INACTIVE_STATUSES } from 'tio-data/data/resources/pslf-form.constants';

function getStatusChipIntent(status: string): ChipIntent {
  switch (status) {
    case 'submitted':
    case 're_submitted':
    case 'submit_pending':
    case 're_submit_pending':
      return 'default';
    case 'borrower_signed':
      return 'warning';
    case 'employer_signed':
    case 'awaiting_submit':
      return 'primary';
    default:
      return 'default';
  }
}

function getStatusLabel(status: string, intl: IntlService): string {
  switch (status) {
    case 'submitted':
    case 'submit_pending':
    case 're_submitted':
    case 're_submit_pending':
      return intl.t('pslf.reimagined.form_status.status.pending_verification');
    case 'borrower_signed':
      return intl.t('pslf.reimagined.form_status.status.awaiting_employer');
    case 'employer_signed':
    case 'awaiting_submit':
      return intl.t('pslf.reimagined.form_status.status.awaiting_submit');
    default:
      return intl.t('pslf.reimagined.form_status.status.processing');
  }
}

function getStatusDescription(form: PslfFormModel, intl: IntlService): string {
  switch (form.status) {
    case 'borrower_signed':
      return intl.t('pslf.reimagined.form_status.current_employer.borrower_signed_description');
    case 'employer_signed':
    case 'awaiting_submit':
      return intl.t('pslf.reimagined.form_status.current_employer.employer_signed_description');
    default: {
      if (!form.submittedAt) {
        return intl.t('pslf.reimagined.form_status.status.pending_verification');
      }
      return intl.t('pslf.reimagined.form_status.current_employer.description', {
        date: intl.formatDate(new Date(form.submittedAt), {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        }),
      });
    }
  }
}

export interface FormStatusAlertsSignature {
  Args: {
    forms: PslfFormModel[];
  };
  Element: HTMLDivElement;
}

export default class PslfFormStatusAlerts extends Component<FormStatusAlertsSignature> {
  @service declare intl: IntlService;

  /**
   * Pick the single most relevant current employer form:
   * - Filter to currentEmployer only, exclude terminal statuses
   * - Sort by createdAt descending (newest first)
   * - Return the first one
   */
  get currentForm(): PslfFormModel | undefined {
    const candidates = this.args.forms
      .filter(
        (f) =>
          f.formType === 'currentEmployer' &&
          !(INACTIVE_STATUSES as readonly string[]).includes(f.status)
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return candidates[0];
  }

  get hasCurrentForm() {
    return !!this.currentForm;
  }

  /**
   * True when the displayed form is expiring (submitted 11+ months ago).
   */
  get isExpiring() {
    return !!this.currentForm?.isExpiring;
  }

  <template>
    <div ...attributes>
      {{#if this.hasCurrentForm}}
        {{#let this.currentForm as |form|}}
          {{#if form}}
            <Card
              @padding="none"
              @roundedXl={{true}}
              class="overflow-hidden"
              data-test-current-employer-alert
            >
              <div
                class="app-status-panel p-4
                  {{if this.isExpiring 'app-status-panel--warning' 'app-status-panel--success'}}"
              >
                <div class="tio-col-tight">
                  <div class="tio-row-tight flex-wrap">
                    {{#if this.isExpiring}}
                      <PhWarning @size={{20}} @weight="fill" class="app-icon-warning shrink-0" />
                    {{else}}
                      <PhCheckCircle
                        @size={{20}}
                        @weight="fill"
                        class="app-icon-success shrink-0"
                      />
                    {{/if}}
                    <Text
                      @variant="inherit"
                      @weight="semibold"
                      class={{if
                        this.isExpiring
                        "app-status-text-warning"
                        "app-status-text-success"
                      }}
                    >
                      {{t "pslf.reimagined.form_status.current_employer.heading"}}
                    </Text>
                    <Chip @intent={{getStatusChipIntent form.status}} @size="sm">
                      {{getStatusLabel form.status this.intl}}
                    </Chip>
                  </div>
                  <Text
                    @variant="inherit"
                    @size="sm"
                    class={{if this.isExpiring "app-status-text-warning" "app-status-text-success"}}
                  >
                    {{#if this.isExpiring}}
                      {{t "pslf.reimagined.form_status.current_employer.expiring_description"}}
                    {{else}}
                      {{getStatusDescription form this.intl}}
                    {{/if}}
                  </Text>
                </div>
              </div>
            </Card>
          {{/if}}
        {{/let}}
      {{/if}}
    </div>
  </template>
}
